import { LearnerF } from '@logic/contexts/Learners/LearnersFrontend.type.ts'
import { FilterObject } from '@logic/useFiltering.hook.ts'
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LxIcon } from '@components/icon/Icon.tsx'
import { ActivitiesOutlineIcon, AnalyticsOutlineIcon, GoalOutlineIcon, TeamMemberIcon, UserInfoIcon } from '@icons/index.ts'
import { EditIcon, MoreActionsIcon } from '@icons/utils'
import styles from '@src/pages/Learner/Learner.module.scss'
import { LxClickAbleIcon } from '@components/icon/clickAbleIcon.tsx'
import sharedStyles from '@src/pages/MultiPage.module.scss'
import LearnerEditStatus from '@src/pages/Learner/LearnerEditStatus.tsx'
import { Link } from 'react-router-dom'
import LearnerInfo from '@src/pages/Learner/LearnerInfo.tsx'
import { useModal } from '@logic/contexts/Modal/ModalContext.tsx'
import { LxDropdown } from '@components/select/dropdown.tsx'
import { isNilOrEmpty } from '@src/utils/isNilOrEmpty.ts'
import { useNotify } from 'react-admin'
import { LearnerEdit } from './Show/CreateOrEdit/LearnerEdit'

interface ExtendedOption extends FilterObject<ReactNode> {
  disabled?: boolean
  disabledMessage?: string
  isLink?: boolean
}

const OptionItem = React.memo<{ icon: ReactNode; label: string; disabled?: boolean }>(({ icon, label, disabled }) => (
  <div className={disabled ? styles.dropdownOptionDisabled : styles.dropdownOption}>
    <LxIcon icon={icon} />
    <span>{label}</span>
  </div>
))

const generateEditOptions = (learner: LearnerF, canEditLearner: boolean, onUpdated?: () => void): ExtendedOption[] => [
  canEditLearner
    ? {
        value: <LearnerEditStatus learner={learner} />,
        optionToRender: <OptionItem icon={EditIcon} label='Manage Status' />,
      }
    : {
        value: undefined,
        disabled: true,
        disabledMessage: 'You don’t have permission to edit learner status',
        optionToRender: <OptionItem icon={EditIcon} label='Manage Status' disabled />,
      },

  canEditLearner
    ? {
        value: <LearnerEdit learner={learner} onUpdated={onUpdated} />,
        optionToRender: <OptionItem icon={EditIcon} label='Edit Info' />,
      }
    : {
        value: undefined,
        disabled: true,
        disabledMessage: 'You don’t have permission to edit learner status',
        optionToRender: <OptionItem icon={EditIcon} label='Edit Info' disabled />,
      },
]

const generateMobileOptions = (learner: LearnerF, canEditLearner: boolean, onUpdated?: () => void): ExtendedOption[] => {
  const baseOptions: ExtendedOption[] = [
     {
      value: null,
      className: sharedStyles.show3column,
      isLink: true,
      // This and following options don't perform an action that returns a value in the same manner, so 'value' might be omitted or handled differently
      optionToRender: (
        <Link to={`${learner.id}/show/goal`} className={styles.dropdownOption}>
          <LxIcon icon={GoalOutlineIcon} />
          Goals
        </Link>
      ),
    },
    {
      value: null,
      className: sharedStyles.show4column,
      isLink: true,
      optionToRender: (
        <Link to={`${learner.id}/show/activity`} className={styles.dropdownOption}>
          <LxIcon icon={ActivitiesOutlineIcon} />
          Activities
        </Link>
      ),
    },
    {
      value: null,
      className: sharedStyles.show5column,
      isLink: true,
      optionToRender: (
        <Link to={`${learner.id}/show/reports`} className={styles.dropdownOption}>
          <LxIcon icon={AnalyticsOutlineIcon} />
          Reports
        </Link>
      ),
    },
    {
      // Assuming showModal is defined elsewhere to handle this action
      value: <LearnerInfo learner={learner} />,
      className: sharedStyles.show2column,
      optionToRender: (
        <div className={styles.dropdownOption}>
          <LxIcon icon={UserInfoIcon}></LxIcon>
          Learner info
        </div>
      ),
    },
        {
      value: null,
      className: sharedStyles.show6column,
      isLink: true,
      optionToRender: (
        <Link to={`${learner.id}/show/members`} className={styles.dropdownOption}>
          <LxIcon icon={TeamMemberIcon} />
          Members
        </Link>
      ),
    },
  ]
  return [...baseOptions, ...generateEditOptions(learner, canEditLearner, onUpdated)]
}

const generateDeskTopOptions = generateEditOptions

type Props = {
  learner: LearnerF
  canEditLearner: boolean
  onUpdated?: () => void
}

export const LearnerListActions: FC<Props> = ({ learner, canEditLearner, onUpdated }) => {
  const [isEditDropdownOpen, setIsEditDropdownOpen] = useState(false)
  const { showModal } = useModal()
  const notify = useNotify()

  const selectRef = useRef<HTMLDivElement>(null)

  const mobileOptions = useMemo(() => generateMobileOptions(learner, canEditLearner, onUpdated), [learner, canEditLearner, onUpdated])
  const deskTopOptions = useMemo(() => generateDeskTopOptions(learner, canEditLearner, onUpdated), [learner, canEditLearner, onUpdated])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsEditDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [selectRef])

  const handleOptionClick = useCallback(
    (option: ExtendedOption) => {
      if (option.disabled) {
        notify(option.disabledMessage || 'Action not available', { type: 'warning' })
        setIsEditDropdownOpen(false)
        return
      }

      if (option.isLink) {
        setIsEditDropdownOpen(false)
        return
      }

      if (!isNilOrEmpty(option.value)) {
        showModal(option.value)
        setIsEditDropdownOpen(false)
      }
    },
    [notify, showModal]
  )

  return (
    <div ref={selectRef}>
      <div className={styles.desktopActions}>
        <LxClickAbleIcon
          icon={MoreActionsIcon}
          dropdown={
            isEditDropdownOpen ? (
              <LxDropdown<ReactNode> className={styles.dropdown} options={deskTopOptions} optionRenderer={(option) => option.optionToRender} onOptionClick={handleOptionClick} />
            ) : null
          }
          onClick={() => setIsEditDropdownOpen((prev) => !prev)}
        />
      </div>
      <div className={styles.mobileActions}>
        <LxClickAbleIcon
          icon={MoreActionsIcon}
          dropdown={
            isEditDropdownOpen ? (
              <LxDropdown<ReactNode> className={styles.dropdown} options={mobileOptions} optionRenderer={(option) => option.optionToRender} onOptionClick={handleOptionClick} />
            ) : null
          }
          onClick={() => setIsEditDropdownOpen((prev) => !prev)}
        />
      </div>
    </div>
  )
}

export const LearnerListActionsHeader: FC<{}> = () => {
  return (
    <>
      <div className={styles.desktopActions}>Actions</div>
      <div className={styles.mobileActions}>More Actions</div>
    </>
  )
}
